import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Formats an integer cent amount using the shop's `money_format` string
 * (the same convention Shopify's own `Shopify.formatMoney` helper uses),
 * so currency symbol/decimals/thousands separators always match the store
 * — nothing here is hardcoded.
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return '';

  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholder);
  const kind = match ? match[1] : 'amount';

  const amount = Math.abs(cents / 100);
  const decimals = kind === 'amount_no_decimals' ? 0 : 2;
  const [whole, fraction] = amount.toFixed(decimals).split('.');
  const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const value = fraction ? `${withThousands}.${fraction}` : withThousands;
  const sign = cents < 0 ? '-' : '';

  return (format || '{{amount}}').replace(placeholder, sign + value);
}

/**
 * Generates a bundle id unique to this Add to Cart click. Every click gets
 * a fresh id (even for the exact same size combination) so repeat adds of
 * the same bundle stay as separate groups in the cart, per spec.
 * @returns {string}
 */
function createBundleId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * A custom element with two modes, switched via the `data-bundle-mode`
 * attribute (set from `bundle_ready` in the section):
 *  - Bundle mode: pick a Top and a Bottom variant (as button-style size
 *    pickers — sold-out sizes render greyed out and unclickable via the
 *    native `disabled` attribute) and add both to the cart in one request,
 *    tagged with a shared `_bundle_id` line-item property so the cart
 *    drawer can display them as one grouped bundle card (see
 *    snippets/cart-products.liquid and assets/component-cart-items.js).
 *  - Single-product mode: a generic option-value picker (any number of
 *    option groups, e.g. size and/or color) that resolves the matching
 *    variant client-side from an embedded JSON variants list and adds
 *    that single variant to the cart.
 */
class LumiereBundleProduct extends HTMLElement {
  /** @type {boolean} */
  #submitting = false;

  /** @type {boolean} */
  bundleMode = true;

  /** @type {HTMLElement | null} */
  topGroup = null;

  /** @type {HTMLElement | null} */
  bottomGroup = null;

  /** @type {HTMLElement[]} */
  optionGroups = [];

  /** @type {any[]} */
  singleVariants = [];

  /** @type {any | null} */
  currentSingleVariant = null;

  /** @type {HTMLButtonElement | null} */
  submitButton = null;

  /** @type {HTMLElement | null} */
  submitText = null;

  /** @type {HTMLElement | null} */
  messageEl = null;

  /** @type {HTMLElement | null} */
  priceEl = null;

  /** @type {HTMLElement | null} */
  compareEl = null;

  /** @type {string} */
  moneyFormat = '{{amount}}';

  /** @type {string} */
  addToCartText = 'Add to cart';

  /** @type {IntersectionObserver | null} */
  #galleryObserver = null;

  connectedCallback() {
    this.bundleMode = this.dataset.bundleMode === 'true';

    this.topGroup = /** @type {HTMLElement | null} */ (this.querySelector('[data-role="top"]'));
    this.bottomGroup = /** @type {HTMLElement | null} */ (this.querySelector('[data-role="bottom"]'));
    this.optionGroups = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-role="option-group"]')));
    this.submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-role="submit"]'));
    this.submitText = /** @type {HTMLElement | null} */ (this.querySelector('[data-role="submit-text"]'));
    this.messageEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-role="message"]'));
    this.priceEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-role="current"]'));
    this.compareEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-role="compare"]'));

    if (!this.bundleMode) {
      const variantsScript = this.querySelector('[data-role="single-variants"]');
      try {
        this.singleVariants = variantsScript ? JSON.parse(variantsScript.textContent || '[]') : [];
      } catch (error) {
        console.error('[lumiere-bundle-product] failed to parse single-variants JSON:', error);
        this.singleVariants = [];
      }
    }

    try {
      this.#initCareDialog();
    } catch (error) {
      console.error('[lumiere-bundle-product] care dialog init failed:', error);
    }

    try {
      this.#initGallery();
    } catch (error) {
      console.error('[lumiere-bundle-product] gallery init failed:', error);
    }

    const { submitButton } = this;
    if (!submitButton) return;
    if (this.bundleMode && (!this.topGroup || !this.bottomGroup)) return;

    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.addToCartText = this.dataset.addToCartText || this.submitText?.textContent || 'Add to cart';

    // Delegated on the root element (not per-group) so it works whether
    // there are exactly two fixed groups (bundle mode: top/bottom) or any
    // number of generic option groups (single-product mode: size, color, ...).
    this.addEventListener('click', this.#onOptionClick);
    submitButton.addEventListener('click', this.#onSubmit);

    this.#onSelectionChange();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onOptionClick);
    this.submitButton?.removeEventListener('click', this.#onSubmit);
    this.#galleryObserver?.disconnect();
  }

  /**
   * Wires the media viewport (mobile horizontal scroll-snap carousel) to
   * the dot pagination: clicking a dot scrolls to that slide, and swiping
   * keeps the active dot in sync via IntersectionObserver. Desktop stacks
   * images and scrolls the page instead (see the section's own CSS), so
   * the dots are hidden there and this wiring is simply inert.
   */
  #initGallery() {
    const viewport = /** @type {HTMLElement | null} */ (this.querySelector('[data-role="viewport"]'));
    const slides = /** @type {HTMLElement[]} */ (
      viewport ? Array.from(viewport.querySelectorAll('[data-role="slide"]')) : []
    );
    const dots = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-role="dot"]')));

    if (!viewport || slides.length === 0) return;

    /** @param {number} index */
    const goTo = (index) => {
      slides[index]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => goTo(Number(dot.dataset.index)));
    });

    if (dots.length === 0 || slides.length < 2 || !('IntersectionObserver' in window)) return;

    /** @param {number} index */
    const setActive = (index) => {
      dots.forEach((dot) => {
        dot.toggleAttribute('data-selected', Number(dot.dataset.index) === index);
      });
    };

    this.#galleryObserver = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!mostVisible) return;

        const index = slides.indexOf(/** @type {HTMLElement} */ (mostVisible.target));
        if (index > -1) setActive(index);
      },
      { root: viewport, threshold: 0.6 }
    );

    slides.forEach((slide) => this.#galleryObserver?.observe(slide));
  }

  /**
   * Wires the care instructions trigger to a native <dialog>. showModal()
   * renders it in the browser's top layer — Escape-to-close, backdrop
   * dimming (::backdrop, styled in the section's CSS), and focus trapping
   * are all handled natively by the browser, so there's nothing left here
   * that can silently get out of sync with the DOM.
   */
  #initCareDialog() {
    const trigger = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-role="care-trigger"]'));
    if (!trigger) return;

    const dialogId = trigger.dataset.careDialogId;
    const dialog = /** @type {HTMLDialogElement | null} */ (
      (dialogId && document.getElementById(dialogId)) || this.querySelector('[data-role="care-dialog"]')
    );
    if (!dialog || typeof dialog.showModal !== 'function') return;

    const closeButton = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-role="care-close"]'));

    trigger.addEventListener('click', () => {
      if (!dialog.open) dialog.showModal();
    });

    closeButton?.addEventListener('click', () => dialog.close());

    // A click on ::backdrop is reported as a click on the <dialog> element
    // itself (::backdrop has no separate hit-testable node) — only close
    // when the click actually landed outside the dialog's own box, so
    // clicking the image/close button doesn't also trigger this.
    dialog.addEventListener('click', (event) => {
      const rect = dialog.getBoundingClientRect();
      const withinDialog =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!withinDialog) dialog.close();
    });
  }

  /**
   * @param {HTMLElement | null} group
   * @returns {HTMLElement | null}
   */
  #selectedOption(group) {
    return /** @type {HTMLElement | null} */ (group?.querySelector('.lbp-option[data-selected]') ?? null);
  }

  /** @param {MouseEvent} event */
  #onOptionClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('.lbp-option') : null);
    if (!button || button.hasAttribute('disabled')) return;

    const group = button.parentElement;
    if (!group) return;

    group.querySelectorAll('.lbp-option').forEach((option) => {
      option.removeAttribute('data-selected');
      option.setAttribute('aria-checked', 'false');
    });

    button.setAttribute('data-selected', '');
    button.setAttribute('aria-checked', 'true');

    this.#onSelectionChange();
  };

  #onSelectionChange = () => {
    if (this.bundleMode) {
      this.#updateBundlePrice();
      this.#updateBundleSubmitState();
    } else {
      this.#updateSingleOptionAvailability();
      this.#updateSinglePrice();
      this.#updateSingleSubmitState();
    }
  };

  #updateBundlePrice() {
    if (!this.priceEl) return;

    const topOption = this.#selectedOption(this.topGroup);
    const bottomOption = this.#selectedOption(this.bottomGroup);
    if (!topOption || !bottomOption) return;

    const topPrice = Number(topOption.dataset.price) || 0;
    const bottomPrice = Number(bottomOption.dataset.price) || 0;
    const topCompare = Number(topOption.dataset.compareAtPrice) || topPrice;
    const bottomCompare = Number(bottomOption.dataset.compareAtPrice) || bottomPrice;

    const combinedPrice = topPrice + bottomPrice;
    const combinedCompare = Math.max(topCompare, topPrice) + Math.max(bottomCompare, bottomPrice);

    this.priceEl.textContent = formatMoney(combinedPrice, this.moneyFormat);

    if (this.compareEl) {
      if (combinedCompare > combinedPrice) {
        this.compareEl.textContent = formatMoney(combinedCompare, this.moneyFormat);
        this.compareEl.hidden = false;
      } else {
        this.compareEl.hidden = true;
      }
    }
  }

  #updateBundleSubmitState() {
    if (!this.submitButton) return;

    const topOption = this.#selectedOption(this.topGroup);
    const bottomOption = this.#selectedOption(this.bottomGroup);
    const canAdd = Boolean(topOption) && Boolean(bottomOption);

    this.submitButton.disabled = !canAdd || this.#submitting;
  }

  /**
   * Resolves the single product's variant matching the currently selected
   * value in each option group (by position — group N's selection maps to
   * variant.options[N], same order Shopify's variant JSON already uses).
   * With zero option groups (a true ONE SIZE product with no real options)
   * `selected` stays empty and every variant matches trivially, which
   * correctly resolves to the product's one and only variant.
   * @returns {any | null}
   */
  #findSingleVariant() {
    /** @type {Record<number, string>} */
    const selected = {};
    this.optionGroups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      const selectedButton = group.querySelector('.lbp-option[data-selected]');
      if (selectedButton instanceof HTMLElement && selectedButton.dataset.optionValue !== undefined) {
        selected[index] = selectedButton.dataset.optionValue;
      }
    });

    const entries = Object.entries(selected);
    return (
      this.singleVariants.find((variant) =>
        entries.every(([index, value]) => variant.options?.[Number(index)] === value)
      ) ?? null
    );
  }

  /**
   * Greys out (disables) any option value for which no available variant
   * exists at all, anywhere in the matrix — a simple, non-combinatorial
   * check (it doesn't re-filter against the other groups' current
   * selection). This is intentionally the simple version: the authoritative
   * gate for whether Add to Cart is actually clickable is the *resolved*
   * variant's own availability in #updateSingleSubmitState, so a shopper
   * can never actually submit an unavailable combination even if a pill
   * that's only invalid in combination with another wasn't pre-emptively
   * greyed out here.
   */
  #updateSingleOptionAvailability() {
    this.optionGroups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      group.querySelectorAll('.lbp-option').forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;
        const value = button.dataset.optionValue;
        const hasAvailableVariant = this.singleVariants.some(
          (variant) => variant.options?.[index] === value && variant.available
        );
        button.disabled = !hasAvailableVariant;
        button.setAttribute('aria-disabled', String(!hasAvailableVariant));
      });
    });
  }

  #updateSinglePrice() {
    if (!this.priceEl) return;

    const variant = this.#findSingleVariant();
    this.currentSingleVariant = variant;
    if (!variant) return;

    const price = Number(variant.price) || 0;
    const compare = Number(variant.compare_at_price) || price;

    this.priceEl.textContent = formatMoney(price, this.moneyFormat);

    if (this.compareEl) {
      if (compare > price) {
        this.compareEl.textContent = formatMoney(compare, this.moneyFormat);
        this.compareEl.hidden = false;
      } else {
        this.compareEl.hidden = true;
      }
    }
  }

  #updateSingleSubmitState() {
    if (!this.submitButton) return;

    const variant = this.currentSingleVariant ?? this.#findSingleVariant();
    const canAdd = Boolean(variant) && Boolean(variant.available);

    this.submitButton.disabled = !canAdd || this.#submitting;
  }

  /**
   * Collects the section ids of every cart-items-component on the page
   * (drawer and/or full cart page), matching the pattern product-form.js
   * uses, so the Ajax response includes fresh HTML for all of them.
   * @returns {string[]}
   */
  #cartSectionIds() {
    const ids = new Set(['cart-drawer-section']);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  /**
   * Fetches the full cart, the same way product-form.js does, so the
   * dispatched CartLinesUpdateEvent carries real totals/quantities for
   * listeners like the cart bubble.
   */
  async #fetchCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItemsComponent).fetchCartData();
    }

    const response = await fetch(`${this.dataset.cartUrl}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  #setMessage(text, state) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.dataset.state = state;
  }

  /** @param {boolean} isLoading */
  #setLoading(isLoading) {
    this.#submitting = isLoading;

    if (this.submitButton) {
      this.submitButton.dataset.loading = String(isLoading);
      this.submitButton.disabled = isLoading;
    }

    if (this.submitText) {
      this.submitText.textContent = isLoading ? this.dataset.loadingText || 'Adding...' : this.addToCartText;
    }
  }

  /**
   * @returns {Array<{id: number, quantity: number, properties: Record<string, string>}> | null}
   */
  #buildBundleItems() {
    const topOption = this.#selectedOption(this.topGroup);
    const bottomOption = this.#selectedOption(this.bottomGroup);
    if (!topOption || !bottomOption) return null;

    const bundleId = createBundleId();
    const bundleTitle = this.dataset.bundleTitle || document.title;
    const bundleImage = this.dataset.bundleImage || '';
    const bundleUrl = this.dataset.bundleUrl || window.location.pathname;

    /** @param {string} role */
    const propertiesFor = (role) => ({
      _bundle_id: bundleId,
      _bundle_title: bundleTitle,
      _bundle_role: role,
      _bundle_display_image: bundleImage,
      _bundle_product_url: bundleUrl,
    });

    return [
      { id: Number(topOption.dataset.variantId), quantity: 1, properties: propertiesFor('top') },
      { id: Number(bottomOption.dataset.variantId), quantity: 1, properties: propertiesFor('bottom') },
    ];
  }

  /**
   * @returns {Array<{id: number, quantity: number}> | null}
   */
  #buildSingleItems() {
    const variant = this.currentSingleVariant ?? this.#findSingleVariant();
    if (!variant || !variant.available) return null;

    return [{ id: Number(variant.id), quantity: 1 }];
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#submitting) return;

    const items = this.bundleMode ? this.#buildBundleItems() : this.#buildSingleItems();
    if (!items) return;

    this.#setLoading(true);
    this.#setMessage('', '');

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: this.#cartSectionIds().join(','), sections_url: window.location.pathname }),
      }).then((res) => res.json());

      if (response.status) {
        const errorMessage = response.description || response.message || this.dataset.errorText || 'Something went wrong.';

        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );

        this.#fetchCart()
          .then((cart) =>
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { didError: true, items: cart.items, source: 'lumiere-bundle-product', sourceId: this.id },
            })
          )
          .catch(deferred.reject);

        this.#setMessage(errorMessage, 'error');
        return;
      }

      const cart = await this.#fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'lumiere-bundle-product',
          sourceId: this.id,
          sections: response.sections,
          didError: false,
        },
      });

      this.#setMessage(this.dataset.successText || 'Added to cart', 'success');

      // The theme's cart-drawer already auto-opens on this event when the
      // merchant's global "auto open cart drawer" setting is on. This
      // section has its own opt-in setting, so explicitly open it too —
      // calling the theme's own drawer API is a harmless no-op if it's
      // already open.
      if (this.dataset.openCartDrawer === 'true') {
        /** @type {any} */ (document.getElementById('cart-drawer'))?.open?.();
      }
    } catch (error) {
      console.error('[lumiere-bundle-product]', error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#setMessage(this.dataset.errorText || 'Something went wrong. Please try again.', 'error');
    } finally {
      this.#setLoading(false);
      if (this.bundleMode) this.#updateBundleSubmitState();
      else this.#updateSingleSubmitState();
    }
  };
}

if (!customElements.get('lumiere-bundle-product')) {
  customElements.define('lumiere-bundle-product', LumiereBundleProduct);
}
